import { DoubleArrowIcon } from "components/icons/DoubleArrowIcon";
import { SingleArrowIcon } from "components/icons/SingleArrowIcon";
import { motion } from "framer-motion";
import { usePagination } from "hooks/usePagination";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { MultiValue, SingleValue } from "react-select";
import { Button } from "../Button/Button";
import { Combobox, OptionData } from "../Combobox/Combobox";
import { Spinner } from "components/DesignSystem/Loader/loader";

type PaginationProps = {
  totalItemCount: number;
  totalPage: number;
  currentPage: number;
  itemsPerPage: number;
  goToFirstPage: () => void;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  goToLastPage: () => void;
  showOptions?: boolean;
  isLoading?: boolean;
  showLoadingText?: boolean;
  size?: "xsmall" | "small";
  pageQueryKey?: string;
};

const DEFAULT_PAGE_QUERY_KEY = "page";

const Pagination = ({
  totalItemCount = 1,
  totalPage = 1,
  currentPage = 1,
  itemsPerPage = 1,
  goToFirstPage,
  goToPrevPage,
  goToNextPage,
  goToLastPage,
  showOptions = false,
  isLoading = false,
  showLoadingText = false,
  size = "xsmall",
  pageQueryKey = DEFAULT_PAGE_QUERY_KEY,
}: PaginationProps) => {
  const localCurrentPage = currentPage < totalPage ? currentPage : totalPage;
  let startItem = 0;
  const { updateMultiple } = useUpdateQuery();
  if (totalItemCount > 0) {
    startItem = (localCurrentPage - 1) * itemsPerPage + 1;
  }

  const endItem = Math.min(startItem + itemsPerPage - 1, totalItemCount);
  const disableNext = totalPage === localCurrentPage;
  const disablePrev = localCurrentPage === 1;

  const paginationOptions = [
    {
      label: "20 items",
      value: "20",
    },
    {
      label: "50 items",
      value: "50",
    },
    {
      label: "75 items",
      value: "75",
    },
    {
      label: "100 items",
      value: "100",
    },
    {
      label: "300 items",
      value: "300",
    },
  ];
  const query = useQuery();
  const currentPaginationOption = query.get("page_size");
  const selectedOption = paginationOptions.find(
    ({ value: optionValue }) => currentPaginationOption === optionValue
  );
  const handleOptions = (
    values: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (values && !(values instanceof Array)) {
      const { value } = values;

      updateMultiple([
        { query: "page_size", value },
        { query: pageQueryKey, value: "1" },
      ]);
    }
  };

  return (
    <div className="t-flex t-items-center t-gap-1">
      <div
        translate="no"
        className="t-text-body-sm t-text-text-60 t-flex t-gap-1 t-overflow-hidden"
      >
        {isLoading ? (
          <div className="t-flex t-items-center t-justify-center t-gap-1">
            <Spinner size="small" />
            {showLoadingText && (
              <div className="t-text-body-sm-italic t-italic">
                Loading results...
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="t-flex t-gap-0.5">
              <motion.div
                key="startItem"
                layout
                initial={{ y: "60px" }}
                animate={{ y: 0 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  damping: 30,
                  stiffness: 300,
                  duration: 0.1,
                }}
              >
                {startItem}
              </motion.div>
              -
              <motion.div
                key="endItem"
                layout
                initial={{ y: "-60px" }}
                animate={{ y: 0 }}
                transition={{
                  type: "spring",
                  mass: 1,
                  damping: 30,
                  stiffness: 300,
                  duration: 0.1,
                }}
              >
                {endItem}
              </motion.div>
            </div>

            <div className="t-whitespace-nowrap">of {totalItemCount}</div>
          </>
        )}
      </div>
      <span className="t-flex t-gap-1 t-items-center">
        <Button
          size="small"
          customType="ghost_icon"
          className="sm:!t-h-6 sm:!t-w-6"
          disabled={disablePrev}
          onClick={goToFirstPage}
        >
          <span className="t-sr-only">Go to first page</span>
          <DoubleArrowIcon />
        </Button>
        <Button
          size="small"
          customType="ghost_icon"
          className="sm:!t-h-6 sm:!t-w-6"
          disabled={disablePrev}
          onClick={goToPrevPage}
        >
          <span className="t-sr-only">Go to previous page</span>
          <SingleArrowIcon />
        </Button>
        <span className="t-rotate-180">
          <Button
            size="small"
            customType="ghost_icon"
            className="sm:!t-h-6 sm:!t-w-6"
            disabled={disableNext}
            onClick={goToNextPage}
          >
            <span className="t-sr-only">Go to next page</span>
            <SingleArrowIcon />
          </Button>
        </span>
        <span className="t-rotate-180">
          <Button
            size="small"
            customType="ghost_icon"
            className="sm:!t-h-6 sm:!t-w-6"
            disabled={disableNext}
            onClick={goToLastPage}
          >
            <span className="t-sr-only">Go to last page</span>
            <DoubleArrowIcon />
          </Button>
        </span>

        {showOptions && (
          <Combobox
            menuPortalTarget={document.body}
            options={paginationOptions}
            onChange={handleOptions}
            isClearable={false}
            isSearchable={false}
            value={
              selectedOption
                ? {
                    label: selectedOption?.value,
                    value: selectedOption?.value,
                  }
                : {
                    label: "20",
                    value: "20",
                  }
            }
            styles={{
              menu: () => ({ width: "100px" }),
            }}
            size={size}
          />
        )}
      </span>
    </div>
  );
};

type PaginationHandlerProps = {
  page_num: number;
  per_page: number;
  total_count: number;
  total_pages: number;
  onPageNumChange?: (page: number) => void;
  isLoading?: boolean;
  showOptions?: boolean;
  showLoadingText?: boolean;
  size?: "xsmall" | "small";
  pageQueryKey?: string;
};

export const PaginationHandler = ({
  page_num,
  per_page,
  total_count,
  total_pages,
  onPageNumChange,
  isLoading = false,
  showOptions = false,
  showLoadingText = false,
  size = "xsmall",
  pageQueryKey = DEFAULT_PAGE_QUERY_KEY,
}: PaginationHandlerProps) => {
  const { update } = useUpdateQuery();

  const handlePageNumChange = (page: number) => {
    if (onPageNumChange) {
      onPageNumChange(page);
      return;
    }

    update({ query: pageQueryKey, value: page });
  };

  const { goToFirstPage, goToLastPage, goToNextPage, goToPrevPage } =
    usePagination({
      pageNumber: page_num,
      totalPage: total_pages,
      onPageNumChange: handlePageNumChange,
    });

  return (
    <Pagination
      currentPage={page_num || 1}
      goToFirstPage={goToFirstPage}
      goToLastPage={goToLastPage}
      goToNextPage={goToNextPage}
      goToPrevPage={goToPrevPage}
      itemsPerPage={per_page || 1}
      totalItemCount={total_count || 0}
      totalPage={total_pages || 1}
      isLoading={isLoading}
      showOptions={showOptions}
      showLoadingText={showLoadingText}
      size={size}
      pageQueryKey={pageQueryKey}
    />
  );
};
